import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import BasicButton from '@/ui-components/BasicButton.vue';
import BaseModal from '@/components/modals/vfm/BaseModal.vue';
import ListItemExpandable from '@/ui-components/ListItemExpandable.vue';
import PackageRow from '@/components/modals/package-selection/PackageRow.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { useI18n } from '@/helpers/composables/useI18n';

import { useQuery, useMutation } from '@/helpers/composables/useApollo';

import {
	SelectAddonDocument,
	SelectAddonMutation,
	SelectAddonMutationVariables,
} from '@/components/modals/package-selection/graphql/mutations/SelectAddon.mutation';
import {
	SelectBundleDocument,
	SelectBundleMutation,
	SelectBundleMutationVariables,
} from '@/components/modals/package-selection/graphql/mutations/SelectBundle.mutation';
import {
	GetPackageDetailsDocument,
	GetPackageDetailsQuery,
	GetPackageDetailsQueryVariables,
} from '@/components/modals/package-selection/graphql/queries/GetPackageDetails.query';

import { Bundle, Package } from '@/@types/graphql-types';
import { toastError } from '@/helpers/vfm/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PackagesTracker } from '@/components/modals/package-selection/enums';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'PackageExtrasModal',
  props: {
    packageId: null,
    onPackageToggle: null
  },
  emits: ['dismiss'],
  setup(__props: any, { emit: emits }) {

const props = __props as {
	packageId: string;
	onPackageToggle: (packageItem: Package | Bundle) => void;
};




const { tc } = useI18n();
const { country } = storeToRefs(useLanguageStore());

const queryVariables = computed(() => ({
	id: props.packageId,
	country: country.value,
}));
const { result, loading } = useQuery<GetPackageDetailsQuery, GetPackageDetailsQueryVariables>(
	GetPackageDetailsDocument,
	queryVariables,
	{ prefetch: false }
);
const { mutate: selectAddonMutate } = useMutation<SelectAddonMutation, SelectAddonMutationVariables>(
	SelectAddonDocument
);
const { mutate: selectBundleMutate } = useMutation<SelectBundleMutation, SelectBundleMutationVariables>(
	SelectBundleDocument
);

const packageItem = computed<Package>(() => {
	const packageDetails: GetPackageDetailsQuery['node'] = result.value?.node;

	return (packageDetails?.__typename === 'Package' ? packageDetails : {}) as Package;
});

const addons = computed<Package[]>(() => (packageItem.value.addons || []) as Package[]);
const selectedAddons = computed<Package[]>(() => addons.value.filter(a => a.selected));
const bundles = computed<Bundle[]>(() => (packageItem.value.bundles || []) as Bundle[]);
const selectedBundles = computed<Bundle[]>(() => bundles.value.filter(b => b.selected));

const countLabels = computed(() => {
	const addonsLabel =
		addons.value.length === 1
			? tc('WEBAPP_PACKAGES_X_ADDONS', 1, { count: addons.value.length })
			: tc('WEBAPP_PACKAGES_X_ADDONS', 2, { count: addons.value.length });

	const bundlesLabel =
		bundles.value.length === 1
			? tc('WEBAPP_PACKAGES_X_BUNDLES', 1, { count: bundles.value.length })
			: tc('WEBAPP_PACKAGES_X_BUNDLES', 2, { count: bundles.value.length });

	return { addonsLabel, bundlesLabel };
});

async function toggleAllAddon(isAllSelected: boolean) {
	try {
		await selectAddonMutate({
			addonShortNames: addons.value.map(a => a.shortName),
			state: !isAllSelected,
			country: country.value,
		});

		TrackingHelper.trackEvent(
			'user_account',
			{
				action: PackagesTracker.TOGGLE_ALL_ADDONS,
				property: PackagesTracker.SETTINGS,
				label: String(packageItem.value.id),
				value: Number(!isAllSelected),
			},
			addons.value.map(addon =>
				TrackingHelper.getPackageContext(
					addon.packageId,
					addon.technicalName,
					'package',
					packageItem.value.packageId,
					packageItem.value.technicalName
				)
			)
		);
	} catch (e) {
		showErrorToast();
	}
}

async function toggleAddon(addon: Package) {
	try {
		await selectAddonMutate({
			addonShortName: addon.shortName,
			state: !addon.selected,
			country: country.value,
		});

		TrackingHelper.trackEvent(
			'user_account',
			{
				action: PackagesTracker.DETAILS_TOGGLED,
				property: PackagesTracker.SETTINGS,
				label: String(addon.packageId),
				value: Number(!addon.selected),
			},
			[
				TrackingHelper.getPackageContext(
					addon.packageId,
					addon.technicalName,
					'package',
					packageItem.value.packageId,
					packageItem.value.technicalName
				),
			]
		);
	} catch (e) {
		showErrorToast();
	}
}

async function toggleBundle(bundle: Bundle) {
	try {
		await selectBundleMutate({
			state: !bundle.selected,
			country: country.value,
			bundleShortName: bundle.shortName,
		});

		TrackingHelper.trackEvent(
			'user_account',
			{
				action: PackagesTracker.DETAILS_TOGGLED,
				property: PackagesTracker.SETTINGS,
				label: String(packageItem.value.packageId),
				value: Number(!bundle.selected),
			},
			bundle.packages.map(bundlePackage =>
				TrackingHelper.getPackageContext(
					bundlePackage.packageId,
					bundlePackage.technicalName,
					'bundle',
					bundle.bundleId,
					bundle.technicalName
				)
			)
		);
	} catch (e) {
		showErrorToast();
	}
}

function onDone() {
	TrackingHelper.trackEvent(
		'user_account',
		{
			action: PackagesTracker.PACKAGE_DETAILS,
			property: PackagesTracker.SETTINGS,
			label: String(packageItem.value.packageId),
			value: 1,
		},
		[TrackingHelper.getPackageContext(packageItem.value.packageId, packageItem.value.technicalName)]
	);

	emits('dismiss');
}

function showErrorToast() {
	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
}

return { __sfc: true,props, emits, tc, country, queryVariables, result, loading, selectAddonMutate, selectBundleMutate, packageItem, addons, selectedAddons, bundles, selectedBundles, countLabels, toggleAllAddon, toggleAddon, toggleBundle, onDone, showErrorToast, BasicButton, BaseModal, ListItemExpandable, PackageRow, Spinner }
}

})